/**
 * PortalAppearanceSettings - Appearance settings for Customer Portal
 */
import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Palette, ArrowLeft, Save, Loader2, Upload, Globe, Sparkles, Check, X, Sun, Moon, Wand2, LogIn, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import BrandPreview from '@/components/settings/BrandPreview';
import EmailLayoutThumbnail from '@/components/email/EmailLayoutThumbnail';
import { Switch } from '@/components/ui/switch';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const colorPresets = [
  { name: 'Grün (Standard)', primary: '#22c55e', secondary: '#10b981', bgFrom: '#f0fdf4', bgTo: '#dcfce7' },
  { name: 'Blau', primary: '#3b82f6', secondary: '#2563eb', bgFrom: '#eff6ff', bgTo: '#dbeafe' },
  { name: 'Lila', primary: '#8b5cf6', secondary: '#7c3aed', bgFrom: '#f5f3ff', bgTo: '#ede9fe' },
  { name: 'Orange', primary: '#f97316', secondary: '#ea580c', bgFrom: '#fff7ed', bgTo: '#ffedd5' },
  { name: 'Rot', primary: '#ef4444', secondary: '#dc2626', bgFrom: '#fef2f2', bgTo: '#fee2e2' },
  { name: 'Türkis', primary: '#14b8a6', secondary: '#0d9488', bgFrom: '#f0fdfa', bgTo: '#ccfbf1' },
];

const BUILD_MSGS = [
  'Webseite wird analysiert…',
  'Logo wird gesucht…',
  'Farben werden erkannt…',
  'Texte werden gelesen…',
  'Vorschau wird erstellt…',
];

// Hellt eine Hex-Farbe auf (für bessere Lesbarkeit auf dunklem Grund).
const lightenHex = (hex, amt = 0.3) => {
  if (!hex || !/^#?[0-9a-fA-F]{3,6}$/.test(hex)) return hex;
  let h = hex.replace('#', '');
  if (h.length === 3) h = h.split('').map((c) => c + c).join('');
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  const up = (v) => Math.round(v + (255 - v) * amt).toString(16).padStart(2, '0');
  return `#${up(r)}${up(g)}${up(b)}`;
};

const FONT_OPTIONS = ['Outfit', 'Inter', 'Poppins', 'Montserrat', 'Roboto', 'Lato', 'Playfair Display', 'Nunito'];

// WCAG-Kontrastverhältnis (1 = identisch, 21 = schwarz/weiss).
const hexRgb = (hex) => {
  let h = (hex || '').replace('#', '');
  if (h.length === 3) h = h.split('').map((c) => c + c).join('');
  if (h.length !== 6) return null;
  return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
};
const relLum = (hex) => {
  const rgb = hexRgb(hex);
  if (!rgb) return null;
  const [r, g, b] = rgb.map((c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};
const contrastRatio = (fg, bg) => {
  const l1 = relLum(fg); const l2 = relLum(bg);
  if (l1 === null || l2 === null) return 21;
  const hi = Math.max(l1, l2); const lo = Math.min(l1, l2);
  return (hi + 0.05) / (lo + 0.05);
};
// Gut lesbare Textfarbe (dunkel/hell) für einen gegebenen Hintergrund.
const idealTextOn = (bg) => {
  const l = relLum(bg);
  if (l === null) return '#ffffff';
  return l > 0.5 ? '#0a0a0a' : '#ffffff';
};
const THEME_DEFAULTS = {
  light: { bg: '#f7faf9', text: '#1f2d2c', muted: '#64748b' },
  dark: { bg: '#0a0a0a', text: '#f2f2ee', muted: '#8a8a85' },
};
// Liefert eine Fehlermeldung, wenn Schrift & Hintergrund zu ähnlich sind.
const portalContrastError = (s) => {
  const mode = s.portal_theme === 'light' ? 'light' : 'dark';
  const d = THEME_DEFAULTS[mode];
  const bg = s.portal_bg_color || d.bg;
  const checks = [
    [s.text_primary || d.text, bg, 'Haupttext und Portal-Hintergrund', 2.5],
    [s.text_secondary || d.muted, bg, 'Hilfstext und Portal-Hintergrund', 2.5],
  ];
  const btn = s.button_color || s.primary_color;
  // Knopf-Text nur bei (nahezu) identischer Farbe blockieren – Weiss auf
  // Marken-Grün/Türkis ist gängig und soll erlaubt bleiben.
  if (hexRgb(btn)) checks.push([s.button_text_color || '#ffffff', btn, 'Knopf-Text und Knopf-Farbe', 1.6]);
  for (const [fg, b, label, min] of checks) {
    if (hexRgb(fg) && hexRgb(b) && contrastRatio(fg, b) < min) {
      return `Zu geringer Kontrast: ${label} sind gleich oder zu ähnlich. Bitte eine deutlich andere Farbe wählen.`;
    }
  }
  return null;
};

const PortalAppearanceSettings = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const logoInputRef = useRef(null);
  const [scrapeUrl, setScrapeUrl] = useState('');
  const [scraping, setScraping] = useState(false);
  const [draft, setDraft] = useState(null);
  const [buildMsg, setBuildMsg] = useState(0);
  const [previewMode, setPreviewMode] = useState('light');
  const [sloganLoading, setSloganLoading] = useState(false);
  const [applyingAccent, setApplyingAccent] = useState(false);
  const [portalView, setPortalView] = useState('login');
  const [emailLayouts, setEmailLayouts] = useState([]);
  const [settings, setSettings] = useState({
    studio_name: 'Kundenportal',
    studio_subtitle: 'Kundenportal',
    primary_color: '#22c55e',
    secondary_color: '#10b981',
    accent_color: '#059669',
    email_accent_color: '',
    app_accent_color: '',
    email_layout: 'bold',
    email_show_logo: true,
    background_gradient_from: '#f0fdf4',
    background_gradient_to: '#dcfce7',
    logo_url: '',
    button_color: '',
    button_text_color: '#ffffff',
    text_primary: '',
    text_secondary: '',
    border_color: '',
    success_color: '#22c55e',
    warning_color: '#f59e0b',
    error_color: '#ef4444',
    calendar_free_color: '#22c55e',
    calendar_selected_color: '',
    calendar_booked_color: '#6b7280',
    calendar_today_color: '#3b82f6',
    corner_radius: 12,
    enable_shadows: true,
    font_heading: 'Outfit',
    font_body: 'Inter',
  });

  useEffect(() => {
    loadSettings();
    loadEmailLayouts();
  }, []);

  const loadEmailLayouts = async () => {
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.get(`${API}/email/templates/layouts`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setEmailLayouts(data.layouts || []);
      if (data.selected) setSettings((prev) => ({ ...prev, email_layout: prev.email_layout || data.selected }));
    } catch (error) {
      console.error('Load email layouts error:', error);
    }
  };

  const loadSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/portal/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings(prev => ({ ...prev, ...response.data }));
    } catch (error) {
      console.error('Load settings error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    const contrastErr = portalContrastError(settings);
    if (contrastErr) {
      toast.error(contrastErr);
      return;
    }
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/portal/settings`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Einstellungen gespeichert');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  // Eine Haupt-Akzentfarbe auf alle Kanäle (App, E-Mail, Portal) übernehmen.
  const applyBrandAccent = async () => {
    const color = settings.brand_accent_color || settings.primary_color;
    setApplyingAccent(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/portal/settings/brand-accent`, { color }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const applied = res.data?.color || color;
      setSettings(prev => ({
        ...prev,
        brand_accent_color: applied,
        primary_color: applied,
        button_color: applied,
        accent_color: applied,
        button_text_color: idealTextOn(applied),
      }));
      toast.success('Marken-Farbe auf alle Kanäle angewendet');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Anwenden fehlgeschlagen');
    } finally {
      setApplyingAccent(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const token = localStorage.getItem('token');
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await axios.post(`${API}/portal/settings/logo`, fd, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSettings((prev) => ({ ...prev, logo_url: data.logo_url }));
      if (data.suggested_color) {
        setSettings((prev) => ({ ...prev, logo_url: data.logo_url, primary_color: data.suggested_color }));
        toast.success(`Logo hochgeladen · Hauptfarbe ${data.suggested_color} vorgeschlagen`);
      } else {
        toast.success('Logo hochgeladen – nicht vergessen zu speichern');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Logo-Upload fehlgeschlagen');
    } finally {
      setUploading(false);
      if (e.target) e.target.value = '';
    }
  };

  useEffect(() => {
    if (!scraping) { setBuildMsg(0); return; }
    const id = setInterval(() => setBuildMsg((m) => (m + 1) % BUILD_MSGS.length), 1200);
    return () => clearInterval(id);
  }, [scraping]);

  const handleScrape = async () => {
    if (!scrapeUrl.trim()) return;
    setScraping(true);
    setDraft(null);
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.post(`${API}/portal/settings/scrape`, { url: scrapeUrl.trim() }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDraft(data.draft);
      toast.success('Vorschau erstellt');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Webseite konnte nicht gelesen werden');
    } finally {
      setScraping(false);
    }
  };

  const applyDraft = async (d) => {
    const merged = { ...settings };
    ['studio_name', 'studio_subtitle', 'logo_url', 'primary_color', 'secondary_color', 'portal_theme', 'portal_bg_color'].forEach((k) => {
      if (d[k]) merged[k] = d[k];
    });
    // Knopf-Textfarbe automatisch lesbar zur (neuen) Akzentfarbe wählen.
    const acc = merged.button_color || merged.primary_color;
    if (acc) merged.button_text_color = idealTextOn(acc);
    setSettings(merged);
    setDraft(null);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/portal/settings`, merged, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Branding übernommen und aktiviert');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Speichern fehlgeschlagen');
    }
  };

  const selectLogo = (u) => setDraft((d) => ({ ...d, logo_url: u }));

  // Kontrast-Automatik: im Dunkelmodus Markenfarbe aufhellen.
  const brand = (base, fallback) => {
    const c = base || fallback;
    return previewMode === 'dark' ? lightenHex(c) : c;
  };

  const generateSlogan = async (scope) => {
    setSloganLoading(true);
    try {
      const token = localStorage.getItem('token');
      const text = scope === 'draft' ? (draft?.studio_subtitle || '') : (settings.studio_subtitle || '');
      const nm = scope === 'draft' ? (draft?.studio_name || '') : (settings.studio_name || '');
      const { data } = await axios.post(`${API}/portal/settings/slogan`, { text, studio_name: nm }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      if (scope === 'draft') setDraft((d) => ({ ...d, studio_subtitle: data.slogan }));
      else setSettings((s) => ({ ...s, studio_subtitle: data.slogan }));
      toast.success('KI-Slogan erstellt');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'KI-Slogan fehlgeschlagen');
    } finally {
      setSloganLoading(false);
    }
  };

  // Kleines Farb-Eingabefeld (Label + Picker + Hex)
  const ColorField = ({ field, label, fallback }) => (
    <div key={field}>
      <Label className="text-xs">{label}</Label>
      <div className="flex gap-2 mt-1">
        <Input type="color" value={settings[field] || fallback || '#000000'}
          onChange={(e) => setSettings({ ...settings, [field]: e.target.value })}
          className="w-12 h-10 p-1 cursor-pointer" data-testid={`token-${field}-picker`} />
        <Input type="text" value={settings[field] || ''} placeholder={fallback || ''}
          onChange={(e) => setSettings({ ...settings, [field]: e.target.value })}
          className="flex-1" data-testid={`token-${field}-hex`} />
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-green-500" />
      </div>
    );
  }

  const contrastErr = portalContrastError(settings);

  return (
    <div className="p-8 max-w-4xl">
      <Button variant="ghost" className="mb-4 -ml-2" onClick={() => navigate('/portal-settings')}>
        <ArrowLeft className="h-4 w-4 mr-2" /> Zurück
      </Button>
      
      <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2 mb-6">
        <Palette className="h-6 w-6 text-green-500" /> Aussehen
      </h1>

      <div className="space-y-6">
        {/* Scraper-Assistent: Branding automatisch aus Webseite */}
        <Card className="border-orange-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-orange-500" /> Branding automatisch erstellen
            </CardTitle>
            <CardDescription>
              Gib die Webseite des Studios ein – wir ziehen Logo, Farben und Texte automatisch heraus.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex gap-2">
              <Input
                value={scrapeUrl}
                onChange={(e) => setScrapeUrl(e.target.value)}
                placeholder="z. B. www.mein-studio.ch"
                disabled={scraping}
                data-testid="scrape-url-input"
                onKeyDown={(e) => { if (e.key === 'Enter') handleScrape(); }}
              />
              <Button
                onClick={handleScrape}
                disabled={scraping || !scrapeUrl.trim()}
                data-testid="scrape-button"
                className="text-white shrink-0"
                style={{ background: 'linear-gradient(135deg, #F97316, #EA580C)' }}
              >
                {scraping ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Globe className="h-4 w-4 mr-2" /> Erstellen</>}
              </Button>
            </div>

            {scraping && (
              <div className="rounded-xl border border-orange-200 bg-orange-50 p-6 text-center" data-testid="scrape-building">
                <Loader2 className="h-8 w-8 animate-spin text-orange-500 mx-auto mb-3" />
                <p className="font-semibold text-slate-800">Kurzer Moment – deine Seite wird errichtet</p>
                <p className="text-sm text-orange-700 mt-1">{BUILD_MSGS[buildMsg]}</p>
              </div>
            )}

            {draft && !scraping && (
              <div className="rounded-xl border border-slate-200 p-4" data-testid="scrape-preview">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-3">Vorschau</p>
                <div className="flex items-start gap-4">
                  <div className="h-16 w-16 rounded-xl border bg-gray-50 flex items-center justify-center overflow-hidden shrink-0">
                    {draft.logo_url
                      ? <img src={draft.logo_url} alt="Logo" className="h-full w-full object-contain" />
                      : <span className="text-[10px] text-gray-400 text-center px-1">kein Logo gefunden</span>}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-slate-900 truncate">{draft.studio_name || '—'}</p>
                    <div className="flex items-start gap-2">
                      <p className="text-sm text-slate-500 flex-1">{draft.studio_subtitle || ''}</p>
                      <button type="button" onClick={() => generateSlogan('draft')} disabled={sloganLoading} data-testid="scrape-slogan-btn"
                        className="shrink-0 inline-flex items-center gap-1 rounded-md border border-orange-200 bg-orange-50 px-2 py-1 text-[11px] font-medium text-orange-700 hover:bg-orange-100 disabled:opacity-60">
                        {sloganLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Wand2 className="h-3 w-3" />} KI-Slogan
                      </button>
                    </div>
                    <div className="flex items-center gap-3 mt-2">
                      {draft.primary_color && (
                        <span className="inline-flex items-center gap-1.5 text-xs text-slate-600">
                          <span className="h-5 w-5 rounded-full border" style={{ background: draft.primary_color }} />
                          {draft.primary_color}
                        </span>
                      )}
                      {draft.secondary_color && (
                        <span className="h-5 w-5 rounded-full border" style={{ background: draft.secondary_color }} />
                      )}
                    </div>
                  </div>
                </div>

                {draft.logos && draft.logos.length > 1 && (
                  <div className="mt-4" data-testid="scrape-logo-variants">
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">Logo wählen</p>
                    <div className="flex flex-wrap gap-2">
                      {draft.logos.map((l, i) => (
                        <button key={i} type="button" onClick={() => selectLogo(l.url)} title={l.label}
                          data-testid={`scrape-logo-option-${i}`}
                          className={`relative h-14 w-14 rounded-lg border bg-white flex items-center justify-center overflow-hidden transition ${draft.logo_url === l.url ? 'border-indigo-500 ring-2 ring-indigo-200' : 'border-slate-200 hover:border-slate-300'}`}>
                          <img src={l.url} alt={l.label} className="h-full w-full object-contain p-1" />
                          {draft.logo_url === l.url && <span className="absolute -top-1.5 -right-1.5 h-4 w-4 rounded-full bg-indigo-500 text-white flex items-center justify-center"><Check className="h-2.5 w-2.5" /></span>}
                        </button>
                      ))}
                    </div>
                    <p className="text-[11px] text-slate-400 mt-1">Favicon, App-Icon, Social-Bild und Header-Logo – wähle die beste Variante.</p>
                  </div>
                )}

                {/* Mini-Landing-Vorschau: „So sieht deine Seite aus" */}
                <div className="mt-5">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">So sieht deine Seite aus</p>
                    <div className="inline-flex rounded-lg border border-slate-200 overflow-hidden" data-testid="preview-mode-toggle">
                      <button type="button" onClick={() => setPreviewMode('light')} data-testid="preview-mode-light"
                        className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs ${previewMode === 'light' ? 'bg-slate-800 text-white' : 'bg-white text-slate-500'}`}>
                        <Sun className="h-3 w-3" /> Hell
                      </button>
                      <button type="button" onClick={() => setPreviewMode('dark')} data-testid="preview-mode-dark"
                        className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs ${previewMode === 'dark' ? 'bg-slate-800 text-white' : 'bg-white text-slate-500'}`}>
                        <Moon className="h-3 w-3" /> Dunkel
                      </button>
                    </div>
                  </div>
                  <div className={`rounded-xl border overflow-hidden shadow-sm ${previewMode === 'dark' ? 'border-slate-700' : 'border-slate-200'}`} data-testid="scrape-landing-preview">
                    {/* Browser-Leiste */}
                    <div className={`flex items-center gap-1.5 px-3 py-2 border-b ${previewMode === 'dark' ? 'bg-slate-800 border-slate-700' : 'bg-slate-100 border-slate-200'}`}>
                      <span className="h-2.5 w-2.5 rounded-full bg-red-400" />
                      <span className="h-2.5 w-2.5 rounded-full bg-yellow-400" />
                      <span className="h-2.5 w-2.5 rounded-full bg-green-400" />
                      <span className={`ml-2 flex-1 truncate rounded px-2 py-0.5 text-[10px] ${previewMode === 'dark' ? 'bg-slate-700 text-slate-300' : 'bg-white text-slate-400'}`}>
                        {(draft.source_url || 'https://dein-studio.ch').replace(/^https?:\/\//, '')}
                      </span>
                    </div>
                    {/* Hero */}
                    <div
                      className="px-6 py-8 text-center text-white"
                      style={{ background: `linear-gradient(135deg, ${brand(draft.primary_color, '#F97316')} 0%, ${brand(draft.secondary_color || draft.primary_color, '#EA580C')} 100%)` }}
                    >
                      <div className="mx-auto mb-3 flex h-16 w-16 items-center justify-center overflow-hidden rounded-xl bg-white/90 p-1.5 shadow">
                        {draft.logo_url
                          ? <img src={draft.logo_url} alt="Logo" className="h-full w-full object-contain" />
                          : <span className="text-base font-bold" style={{ color: draft.primary_color || '#EA580C' }}>{(draft.studio_name || 'S').slice(0, 2).toUpperCase()}</span>}
                      </div>
                      <h3 className="text-xl font-bold leading-tight">{draft.studio_name || 'Dein Studio'}</h3>
                      <p className="mx-auto mt-1 max-w-sm text-sm text-white/85 line-clamp-2">{draft.studio_subtitle || 'Willkommen – buche deinen Termin online.'}</p>
                      <span className="mt-4 inline-block rounded-full bg-white px-5 py-2 text-sm font-semibold" style={{ color: draft.primary_color || '#EA580C' }}>
                        Jetzt Termin buchen
                      </span>
                    </div>
                    {/* Body-Mock */}
                    <div className={`grid grid-cols-3 gap-3 p-4 ${previewMode === 'dark' ? 'bg-slate-900' : 'bg-white'}`}>
                      {[0, 1, 2].map((i) => (
                        <div key={i} className={`rounded-lg border p-3 ${previewMode === 'dark' ? 'border-slate-700' : 'border-slate-100'}`}>
                          <div className="h-2 w-8 rounded-full" style={{ background: brand(draft.primary_color, '#F97316') }} />
                          <div className={`mt-2 h-2 w-full rounded-full ${previewMode === 'dark' ? 'bg-slate-700' : 'bg-slate-100'}`} />
                          <div className={`mt-1.5 h-2 w-2/3 rounded-full ${previewMode === 'dark' ? 'bg-slate-700' : 'bg-slate-100'}`} />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2 mt-4">
                  <Button onClick={() => applyDraft(draft)} data-testid="scrape-approve" className="bg-green-600 hover:bg-green-700 text-white">
                    <Check className="h-4 w-4 mr-2" /> Gefällt mir – übernehmen
                  </Button>
                  <Button variant="outline" onClick={() => setDraft(null)} data-testid="scrape-reject">
                    <X className="h-4 w-4 mr-2" /> Verwerfen
                  </Button>
                </div>
                <p className="text-xs text-gray-400 mt-2">Nicht ganz passend? Übernehmen und die Felder unten anpassen.</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Names */}
        <Card>
          <CardHeader>
            <CardTitle>Branding</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Studio-Name</Label>
              <Input value={settings.studio_name} onChange={(e) => setSettings({...settings, studio_name: e.target.value})} className="mt-1" />
            </div>
            <div>
              <Label>Untertitel</Label>
              <div className="mt-1 flex gap-2">
                <Input value={settings.studio_subtitle} onChange={(e) => setSettings({...settings, studio_subtitle: e.target.value})} className="flex-1" />
                <Button type="button" variant="outline" disabled={sloganLoading} onClick={() => generateSlogan('form')} data-testid="form-slogan-btn" className="shrink-0 gap-1.5">
                  {sloganLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />} KI-Slogan
                </Button>
              </div>
            </div>
            <div>
              <Label>Darstellung (Kundenportal & Einbindungen)</Label>
              <div className="mt-1 inline-flex rounded-lg border border-gray-200 overflow-hidden" data-testid="theme-mode-select">
                {[
                  { v: 'light', label: 'Hell', Icon: Sun },
                  { v: 'dark', label: 'Dunkel', Icon: Moon },
                  { v: 'auto', label: 'Automatisch', Icon: Sparkles },
                ].map(({ v, label, Icon }) => (
                  <button key={v} type="button" onClick={() => setSettings({ ...settings, theme_mode: v })}
                    data-testid={`theme-mode-${v}`}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm ${(settings.theme_mode || 'light') === v ? 'bg-slate-800 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}>
                    <Icon className="h-3.5 w-3.5" /> {label}
                  </button>
                ))}
              </div>
              <p className="text-xs text-gray-400 mt-1">Gilt für das Kundenportal und alle iFrame-Einbindungen. „Automatisch" folgt dem Gerät des Besuchers.</p>
            </div>
            <div>
              <Label>Logo</Label>
              <div className="mt-1 flex items-center gap-4">
                <div className="h-16 w-16 rounded-xl border border-gray-200 bg-gray-50 flex items-center justify-center overflow-hidden shrink-0">
                  {settings.logo_url
                    ? <img src={settings.logo_url} alt="Logo" className="h-full w-full object-contain" data-testid="logo-preview" />
                    : <Palette className="h-6 w-6 text-gray-300" />}
                </div>
                <div>
                  <input ref={logoInputRef} type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" className="hidden" onChange={handleLogoUpload} data-testid="logo-file-input" />
                  <Button type="button" variant="outline" disabled={uploading} onClick={() => logoInputRef.current?.click()} data-testid="logo-upload-button">
                    {uploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
                    {uploading ? 'Lädt hoch…' : 'Logo hochladen'}
                  </Button>
                  <p className="text-xs text-gray-400 mt-1">PNG, JPG, WEBP oder SVG · max. 5 MB</p>
                </div>
              </div>
            </div>
            <div>
              <Label>Logo URL (optional)</Label>
              <Input value={settings.logo_url || ''} onChange={(e) => setSettings({...settings, logo_url: e.target.value})} placeholder="https://… oder nach Upload automatisch" className="mt-1" data-testid="logo-url-input" />
            </div>
          </CardContent>
        </Card>

        {/* Color Presets */}
        <Card>
          <CardHeader>
            <CardTitle>Farbschema</CardTitle>
          </CardHeader>
          <CardContent>
            {/* Haupt-Akzentfarbe: eine Farbe, die auf App, E-Mail und Portal übernommen wird */}
            <div className="rounded-xl border border-gray-200 p-4 mb-6" data-testid="brand-accent-block">
              <Label htmlFor="brand_accent_color" className="text-sm font-medium">Haupt-Akzentfarbe</Label>
              <p className="text-xs text-gray-500 mt-0.5 mb-2">Wird mit einem Klick auf App, E-Mail und Kundenportal übernommen.</p>
              <div className="flex flex-wrap items-center gap-2">
                <Input
                  type="color"
                  id="brand_accent_color"
                  value={settings.brand_accent_color || settings.primary_color || '#22c55e'}
                  onChange={(e) => setSettings({ ...settings, brand_accent_color: e.target.value })}
                  className="w-12 h-10 p-1 cursor-pointer"
                  data-testid="brand-accent-color-picker"
                />
                <Input
                  type="text"
                  value={settings.brand_accent_color || ''}
                  placeholder={settings.primary_color || '#22c55e'}
                  onChange={(e) => setSettings({ ...settings, brand_accent_color: e.target.value })}
                  className="w-40"
                  data-testid="brand-accent-color-text"
                />
                <Button
                  type="button"
                  onClick={applyBrandAccent}
                  disabled={applyingAccent}
                  data-testid="brand-accent-apply-btn"
                >
                  {applyingAccent ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Check className="h-4 w-4 mr-1.5" />}
                  Auf alle Kanäle anwenden
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-3 md:grid-cols-6 gap-3 mb-6">
              {colorPresets.map(preset => (
                <button
                  key={preset.name}
                  onClick={() => setSettings({
                    ...settings,
                    primary_color: preset.primary,
                    secondary_color: preset.secondary,
                    background_gradient_from: preset.bgFrom,
                    background_gradient_to: preset.bgTo
                  })}
                  className="p-3 rounded-lg border-2 hover:border-gray-400 transition-all text-center"
                  style={{ 
                    borderColor: settings.primary_color === preset.primary ? preset.primary : '#e5e7eb',
                    background: `linear-gradient(135deg, ${preset.bgFrom} 0%, ${preset.bgTo} 100%)`
                  }}
                >
                  <div className="w-8 h-8 rounded-full mx-auto mb-2" style={{ backgroundColor: preset.primary }} />
                  <span className="text-xs font-medium text-gray-700">{preset.name}</span>
                </button>
              ))}
            </div>

            {/* Custom Colors */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <Label>Primärfarbe</Label>
                <div className="flex gap-2 mt-1">
                  <Input type="color" value={settings.primary_color} onChange={(e) => setSettings({...settings, primary_color: e.target.value})} className="w-12 h-10 p-1 cursor-pointer" />
                  <Input type="text" value={settings.primary_color} onChange={(e) => setSettings({...settings, primary_color: e.target.value})} className="flex-1" />
                </div>
              </div>
              <div>
                <Label>Sekundärfarbe</Label>
                <div className="flex gap-2 mt-1">
                  <Input type="color" value={settings.secondary_color} onChange={(e) => setSettings({...settings, secondary_color: e.target.value})} className="w-12 h-10 p-1 cursor-pointer" />
                  <Input type="text" value={settings.secondary_color} onChange={(e) => setSettings({...settings, secondary_color: e.target.value})} className="flex-1" />
                </div>
              </div>
              <div>
                <Label>Hintergrund Von</Label>
                <div className="flex gap-2 mt-1">
                  <Input type="color" value={settings.background_gradient_from} onChange={(e) => setSettings({...settings, background_gradient_from: e.target.value})} className="w-12 h-10 p-1 cursor-pointer" />
                  <Input type="text" value={settings.background_gradient_from} onChange={(e) => setSettings({...settings, background_gradient_from: e.target.value})} className="flex-1" />
                </div>
              </div>
              <div>
                <Label>Hintergrund Bis</Label>
                <div className="flex gap-2 mt-1">
                  <Input type="color" value={settings.background_gradient_to} onChange={(e) => setSettings({...settings, background_gradient_to: e.target.value})} className="w-12 h-10 p-1 cursor-pointer" />
                  <Input type="text" value={settings.background_gradient_to} onChange={(e) => setSettings({...settings, background_gradient_to: e.target.value})} className="flex-1" />
                </div>
              </div>
            </div>

            {/* Kundenportal-Hintergrund (hell/dunkel + exakte Farbe) */}
            <div className="mt-6 rounded-xl border border-gray-200 p-4" data-testid="portal-background-block">
              <Label className="text-sm font-medium">Kundenportal- & iFrame-Hintergrund</Label>
              <p className="text-xs text-gray-500 mt-0.5 mb-3">Wird beim Analysieren der Kundenwebseite automatisch erkannt und ist hier jederzeit änderbar. Der iFrame bleibt transparent und passt sich dieser Wahl an.</p>
              <div className="flex flex-wrap items-center gap-3">
                <div className="flex rounded-lg border border-gray-200 overflow-hidden" data-testid="portal-theme-toggle">
                  <button type="button" onClick={() => setSettings({ ...settings, portal_theme: 'light' })}
                    data-testid="portal-theme-light"
                    className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium transition-colors ${(settings.portal_theme || 'dark') === 'light' ? 'bg-green-500 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
                    <Sun className="h-4 w-4" /> Hell
                  </button>
                  <button type="button" onClick={() => setSettings({ ...settings, portal_theme: 'dark' })}
                    data-testid="portal-theme-dark"
                    className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium transition-colors ${(settings.portal_theme || 'dark') === 'dark' ? 'bg-gray-900 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
                    <Moon className="h-4 w-4" /> Dunkel
                  </button>
                </div>
                <div className="flex items-center gap-2">
                  <Label className="text-xs text-gray-500">Hintergrundfarbe</Label>
                  <Input type="color" value={settings.portal_bg_color || ((settings.portal_theme || 'dark') === 'light' ? '#f7faf9' : '#0a0a0a')}
                    onChange={(e) => setSettings({ ...settings, portal_bg_color: e.target.value })}
                    className="w-12 h-10 p-1 cursor-pointer" data-testid="portal-bg-color-picker" />
                  <Input type="text" value={settings.portal_bg_color || ''} placeholder={(settings.portal_theme || 'dark') === 'light' ? '#f7faf9' : '#0a0a0a'}
                    onChange={(e) => setSettings({ ...settings, portal_bg_color: e.target.value })}
                    className="w-32" data-testid="portal-bg-color-text" />
                </div>
              </div>
            </div>

            {contrastErr && (
              <div className="mt-4 rounded-lg border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-700" data-testid="portal-contrast-error">
                {contrastErr}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Kanal-Farben (je Kanal getrennt) + E-Mail-Layout */}
        <Card data-testid="channel-colors-card">
          <CardHeader>
            <CardTitle>Farben je Kanal</CardTitle>
            <CardDescription>
              Stelle die Akzentfarbe für Kundenportal, Mobile-App und E-Mails getrennt ein. Die Vorschau unten zeigt jede Änderung sofort.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                { field: 'primary_color', label: 'Kundenportal', fallback: '#22c55e', help: 'Farbe im Kundenportal & in Einbindungen.' },
                { field: 'app_accent_color', label: 'Mobile-App', fallback: settings.primary_color, help: 'Farbe in der Mobile-App.' },
                { field: 'email_accent_color', label: 'E-Mail', fallback: settings.primary_color, help: 'Farbe in versendeten E-Mails.' },
              ].map(({ field, label, fallback, help }) => (
                <div key={field} className="rounded-xl border border-gray-200 p-4">
                  <Label className="text-sm font-medium">{label}</Label>
                  <div className="flex gap-2 mt-2">
                    <Input type="color" value={settings[field] || fallback || '#22c55e'}
                      onChange={(e) => setSettings({ ...settings, [field]: e.target.value })}
                      className="w-12 h-10 p-1 cursor-pointer" data-testid={`channel-${field}-picker`} />
                    <Input type="text" value={settings[field] || ''} placeholder={fallback || '#22c55e'}
                      onChange={(e) => setSettings({ ...settings, [field]: e.target.value })}
                      className="flex-1" data-testid={`channel-${field}-hex`} />
                  </div>
                  <p className="text-xs text-gray-400 mt-1.5">{help}</p>
                </div>
              ))}
            </div>

            <div className="mb-6 flex items-start justify-between gap-4 rounded-lg border border-gray-200 p-3" data-testid="email-logo-toggle-row">
              <div>
                <Label className="text-sm font-medium">Logo in E-Mails anzeigen</Label>
                <p className="text-xs text-gray-500 mt-0.5">
                  Zeigt dein Studio-Logo (aus „Logo & Marke") oben in jeder E-Mail. Aus = statt Logo erscheint nur der Studioname als Text.
                </p>
              </div>
              <Switch
                data-testid="email-show-logo-switch"
                checked={settings.email_show_logo !== false}
                onCheckedChange={(v) => setSettings({ ...settings, email_show_logo: v })}
              />
            </div>
            <div>
              <Label className="text-sm font-medium">E-Mail-Layout</Label>
              <p className="text-xs text-gray-500 mt-0.5 mb-3">Wähle, wie deine E-Mails aufgebaut sind. Die Vorschau unten (Tab „E-Mail") zeigt das gewählte Layout.</p>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="email-layout-grid">
                {emailLayouts.map((l) => {
                  const selected = (settings.email_layout || 'bold') === l.id;
                  return (
                    <button
                      key={l.id}
                      type="button"
                      onClick={() => setSettings({ ...settings, email_layout: l.id })}
                      data-testid={`email-layout-option-${l.id}`}
                      className={`text-left rounded-xl border-2 p-3 transition-all ${selected ? 'border-green-500 ring-2 ring-green-100' : 'border-gray-200 hover:border-gray-300'}`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-semibold text-gray-900">{l.name}</span>
                        {selected && <Check className="h-4 w-4 text-green-500" />}
                      </div>
                      {/* Originalgetreue Layout-Vorschau */}
                      <EmailLayoutThumbnail
                        layoutId={l.id}
                        accent={settings.email_accent_color || settings.primary_color || '#22c55e'}
                      />
                      <p className="text-[11px] text-gray-400 mt-1.5 leading-tight">{l.description}</p>
                    </button>
                  );
                })}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Marken-Vorschau: E-Mail, Portal, App */}
        <Card data-testid="brand-preview-card">
          <CardContent className="pt-6">
            <BrandPreview settings={settings} />
          </CardContent>
        </Card>

        {/* Erweiterte Design-Tokens (Konzept Kap. 8) */}
        <Card data-testid="design-tokens-card">
          <CardHeader>
            <CardTitle>Erweiterte Gestaltung</CardTitle>
            <CardDescription>Feintuning für Knöpfe, Kalender, Hinweise, Rahmen, Formen und Schrift. Leere Farbfelder nutzen die Primärfarbe.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Knöpfe */}
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">Knöpfe</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {ColorField({ field: 'button_color', label: 'Hauptaktion', fallback: settings.primary_color || '#c9a34e' })}
                {ColorField({ field: 'button_text_color', label: 'Text auf Knopf', fallback: idealTextOn(settings.button_color || settings.primary_color || '#c9a34e') })}
              </div>
            </div>
            {/* Text */}
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">Text</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {ColorField({ field: 'text_primary', label: 'Haupttext', fallback: '#0f172a' })}
                {ColorField({ field: 'text_secondary', label: 'Hilfstext', fallback: '#64748b' })}
              </div>
            </div>
            {/* Kalender & Buchung */}
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">Kalender & Buchung</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {ColorField({ field: 'calendar_free_color', label: 'Frei', fallback: '#22c55e' })}
                {ColorField({ field: 'calendar_selected_color', label: 'Gewählt', fallback: settings.primary_color })}
                {ColorField({ field: 'calendar_booked_color', label: 'Belegt', fallback: '#6b7280' })}
                {ColorField({ field: 'calendar_today_color', label: 'Heute', fallback: '#3b82f6' })}
              </div>
            </div>
            {/* Hinweise */}
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">Hinweise</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {ColorField({ field: 'success_color', label: 'Erfolg', fallback: '#22c55e' })}
                {ColorField({ field: 'warning_color', label: 'Warnung', fallback: '#f59e0b' })}
                {ColorField({ field: 'error_color', label: 'Fehler', fallback: '#ef4444' })}
              </div>
            </div>
            {/* Linien & Formen */}
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">Linien & Formen</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 items-end">
                {ColorField({ field: 'border_color', label: 'Rahmen & Linien', fallback: '#e2e8f0' })}
                <div>
                  <Label className="text-xs">Eckenradius: {settings.corner_radius}px</Label>
                  <input type="range" min="0" max="28" value={settings.corner_radius}
                    onChange={(e) => setSettings({ ...settings, corner_radius: parseInt(e.target.value, 10) })}
                    className="w-full mt-3 accent-green-600" data-testid="token-corner-radius" />
                </div>
                <div className="flex items-center gap-2 pb-2">
                  <input type="checkbox" checked={!!settings.enable_shadows}
                    onChange={(e) => setSettings({ ...settings, enable_shadows: e.target.checked })}
                    className="h-4 w-4 accent-green-600" data-testid="token-shadows" id="tok-shadow" />
                  <Label htmlFor="tok-shadow" className="text-xs cursor-pointer">Schatten anzeigen</Label>
                </div>
              </div>
            </div>
            {/* Schrift */}
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">Schrift</p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs">Überschriften</Label>
                  <select value={settings.font_heading} onChange={(e) => setSettings({ ...settings, font_heading: e.target.value })}
                    className="mt-1 w-full rounded-md border border-gray-200 px-3 py-2 text-sm" data-testid="token-font-heading">
                    {FONT_OPTIONS.map((f) => <option key={f} value={f}>{f}</option>)}
                  </select>
                </div>
                <div>
                  <Label className="text-xs">Fliesstext</Label>
                  <select value={settings.font_body} onChange={(e) => setSettings({ ...settings, font_body: e.target.value })}
                    className="mt-1 w-full rounded-md border border-gray-200 px-3 py-2 text-sm" data-testid="token-font-body">
                    {FONT_OPTIONS.map((f) => <option key={f} value={f}>{f}</option>)}
                  </select>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Preview */}
        <Card>
          <CardHeader>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <CardTitle>Live-Vorschau</CardTitle>
                <CardDescription>So sieht dein Kundenportal mit diesen Farben aus.</CardDescription>
              </div>
              <div className="flex items-center gap-2">
                <div className="inline-flex rounded-lg border border-slate-200 overflow-hidden" data-testid="portal-preview-toggle">
                  <button
                    type="button"
                    data-testid="portal-preview-login-btn"
                    onClick={() => setPortalView('login')}
                    className={`px-3 py-1.5 text-xs font-medium transition-colors ${portalView === 'login' ? 'bg-slate-900 text-white' : 'bg-white text-slate-500 hover:bg-slate-50'}`}>
                    Anmeldung
                  </button>
                  <button
                    type="button"
                    data-testid="portal-preview-portal-btn"
                    onClick={() => setPortalView('portal')}
                    className={`px-3 py-1.5 text-xs font-medium transition-colors ${portalView === 'portal' ? 'bg-slate-900 text-white' : 'bg-white text-slate-500 hover:bg-slate-50'}`}>
                    Portal
                  </button>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  data-testid="open-live-portal-btn"
                  onClick={() => window.open(`${window.location.origin}/portal`, '_blank', 'noopener')}
                >
                  <ExternalLink className="h-3.5 w-3.5 mr-1.5" /> Live-Portal öffnen
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {(() => {
              const isDark = settings.portal_theme !== 'light';
              const accent = settings.button_color || settings.primary_color || '#c9a34e';
              const sel = settings.calendar_selected_color || accent;
              const rad = `${settings.corner_radius}px`;
              const pageBg = isDark ? (settings.portal_bg_color || '#0a0a0a') : (settings.portal_bg_color || '#f7faf9');
              const cardBg = isDark ? '#141414' : '#ffffff';
              const brd = settings.border_color || (isDark ? '#262626' : '#e2e8f0');
              const titleCol = isDark ? '#f2f2ee' : (settings.text_primary || '#0f172a');
              const subCol = isDark ? '#8a8a85' : (settings.text_secondary || '#64748b');
              const inputBg = isDark ? 'rgba(255,255,255,0.05)' : '#ffffff';
              const inputBrd = isDark ? 'rgba(255,255,255,0.12)' : '#cbd5e1';
              const shadow = settings.enable_shadows ? '0 10px 30px rgba(0,0,0,0.25)' : 'none';
              const btnText = idealTextOn(accent);
              const fontBody = `'${settings.font_body}', sans-serif`;
              const fontHead = `'${settings.font_heading}', sans-serif`;

              const Logo = () => (
                <div className="h-14 w-14 flex items-center justify-center overflow-hidden mx-auto" style={{ borderRadius: rad, background: `${accent}20` }}>
                  {settings.logo_url ? <img src={settings.logo_url} alt="Logo" className="h-full w-full object-contain" /> : <Palette className="h-6 w-6" style={{ color: accent }} />}
                </div>
              );

              const LoginView = (
                <div className="mx-auto max-w-sm p-7" style={{ background: cardBg, borderRadius: `calc(${rad} + 6px)`, border: `1px solid ${brd}`, boxShadow: shadow }}>
                  <div className="mb-5"><Logo /></div>
                  <h2 className="text-lg font-semibold flex items-center gap-2" style={{ color: titleCol, fontFamily: fontHead }}>
                    <LogIn className="h-4 w-4" style={{ color: accent }} /> Anmelden
                  </h2>
                  <p className="text-sm mt-1" style={{ color: subCol }}>Gib deine E-Mail ein — wir senden dir einen Code.</p>
                  <div className="mt-5">
                    <div className="text-[11px] font-semibold uppercase tracking-wider mb-1" style={{ color: subCol }}>E-Mail</div>
                    <div className="h-11 flex items-center px-3 text-sm" style={{ background: inputBg, border: `1px solid ${inputBrd}`, borderRadius: rad, color: titleCol }}>
                      max@beispiel.ch
                    </div>
                  </div>
                  <button className="w-full h-11 mt-4 text-sm font-semibold" style={{ background: accent, color: btnText, borderRadius: rad }}>
                    Code senden
                  </button>
                  <p className="text-xs mt-3 text-center" style={{ color: subCol }}>Neu hier? Dein Konto wird automatisch erstellt.</p>
                </div>
              );

              const PortalView = (
                <div className="mx-auto max-w-md p-5" style={{ background: cardBg, borderRadius: `calc(${rad} + 6px)`, border: `1px solid ${brd}`, boxShadow: shadow }}>
                  {/* Header */}
                  <div className="flex items-center gap-3 mb-4">
                    <div className="h-10 w-10 flex items-center justify-center overflow-hidden" style={{ borderRadius: rad, background: `${accent}20` }}>
                      {settings.logo_url ? <img src={settings.logo_url} alt="Logo" className="h-full w-full object-contain" /> : <Palette className="h-5 w-5" style={{ color: accent }} />}
                    </div>
                    <div>
                      <h2 className="text-base font-bold leading-tight" style={{ color: titleCol, fontFamily: fontHead }}>Hallo, Alex 👋</h2>
                      <p className="text-xs" style={{ color: subCol }}>{settings.studio_name}</p>
                    </div>
                  </div>
                  {/* Tabs */}
                  <div className="flex gap-2 mb-4">
                    {['Start', 'Buchen', 'Termine'].map((t, i) => (
                      <span key={t} className="px-3 py-1.5 text-xs font-medium" style={i === 0 ? { background: accent, color: btnText, borderRadius: rad } : { background: `${accent}12`, color: titleCol, borderRadius: rad }}>{t}</span>
                    ))}
                  </div>
                  {/* Nächster Termin */}
                  <div className="p-3 mb-4" style={{ background: isDark ? 'rgba(255,255,255,0.04)' : '#f8fafc', border: `1px solid ${brd}`, borderRadius: rad }}>
                    <div className="text-[11px] uppercase tracking-wider" style={{ color: subCol }}>Nächster Termin</div>
                    <div className="text-sm font-semibold mt-0.5" style={{ color: titleCol }}>Mo, 24. Juni · 18:00</div>
                    <div className="text-xs" style={{ color: subCol }}>Personal Training · Wetzikon</div>
                  </div>
                  {/* Kalender-Chips */}
                  <div className="flex gap-2 mb-4">
                    {[
                      { c: settings.calendar_free_color, t: 'Frei' },
                      { c: sel, t: 'Gewählt' },
                      { c: settings.calendar_booked_color, t: 'Belegt' },
                      { c: settings.calendar_today_color, t: 'Heute' },
                    ].map((s, i) => (
                      <span key={i} className="px-2.5 py-1 text-xs font-medium" style={{ background: s.c, color: idealTextOn(s.c), borderRadius: rad }}>{s.t}</span>
                    ))}
                  </div>
                  <button className="w-full py-2.5 text-sm font-semibold" style={{ background: accent, color: btnText, borderRadius: rad }}>
                    Jetzt Termin buchen
                  </button>
                </div>
              );

              return (
                <div>
                  <div className="rounded-xl p-6" data-testid="design-live-preview" style={{ background: pageBg, fontFamily: fontBody }}>
                    {portalView === 'login' ? LoginView : PortalView}
                  </div>
                  {/* Farb-Legende: wo welche Farbe sitzt */}
                  <div className="mt-4 flex flex-wrap gap-3 text-xs" data-testid="preview-color-legend">
                    {[
                      { c: accent, l: 'Akzent / Knöpfe' },
                      { c: btnText, l: 'Knopf-Text' },
                      { c: pageBg, l: 'Hintergrund' },
                      { c: cardBg, l: 'Karten' },
                      { c: titleCol, l: 'Überschrift' },
                      { c: subCol, l: 'Fliesstext' },
                    ].map((x, i) => (
                      <div key={i} className="inline-flex items-center gap-1.5">
                        <span className="h-4 w-4 rounded border border-slate-300" style={{ background: x.c }} />
                        <span className="text-slate-600">{x.l}</span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}
          </CardContent>
        </Card>

        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={() => navigate('/portal-settings')}>Abbrechen</Button>
          <Button onClick={handleSave} disabled={saving || !!contrastErr} data-testid="save-settings-btn">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            Speichern
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PortalAppearanceSettings;
